import React from "react";
import { Badge, Clock, Wallet, Hourglass, CheckCircle2, XCircle } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import MetricCard from "./MetricCard";
import ChartCard from "./Chartcard";
import EmployeeTable from "./employeetable";

const mockTrendsData = [
  { name: "Jan", hours: 120 },
  { name: "Feb", hours: 150 },
  { name: "Mar", hours: 180 },
  { name: "Apr", hours: 130 },
  { name: "May", hours: 190 },
  { name: "Jun", hours: 220 },
  { name: "Jul", hours: 250 },
  { name: "Aug", hours: 210 },
];

const mockDepartmentData = [
  { name: "Production", hours: 450 },
  { name: "QA", hours: 180 },
  { name: "Engineering", hours: 320 },
  { name: "Sales", hours: 90 },
  { name: "HR", hours: 45 },
];

/**
 * OT (Overtime) Dashboard.
 *
 * All data is passed in as props with sensible defaults matching the
 * original design, so this can be dropped in and later wired up to
 * real data (API calls, React Query, etc.) by the parent.
 */
export default function OTDashboard({
  metrics = {
    totalEmployees: 126,
    totalOtHours: 1845,
    totalOtAmount: "Rs. 1.25M",
    pendingHours: 185,
    approvedHours: 1520,
    rejectedHours: 140,
  },
  topEmployees = [
    { name: "S.A. Chathuri Jeewanthi", serviceNumber: "2400135", otHours: 42.5 },
    { name: "K. Rohitha Wasantha Perera", serviceNumber: "2400110", otHours: 38.0 },
    { name: "F.A. Lakni Fernando", serviceNumber: "2400142", otHours: 35.5 },
  ],
  lowestEmployees = [
    { name: "J.D. Silva", serviceNumber: "2400215", otHours: 2.0 },
    { name: "M.N. Perera", serviceNumber: "2400230", otHours: 4.5 },
    { name: "R.A. de Silva", serviceNumber: "2400255", otHours: 5.5 },
  ],
  trendsChart,
  departmentChart,
  onViewAllTopEmployees,
}) {
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
          <Badge className="w-5 h-5 text-primary-600" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-surface-900 leading-tight">OT Dashboard</h1>
          <p className="text-xs text-surface-400 font-medium">Overtime analytics &amp; insights</p>
        </div>
      </div>

      <div className="space-y-6">
          {/* Metrics grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <MetricCard
              label="Total Employees"
              icon={<Badge className="w-4 h-4" />}
              value={metrics.totalEmployees}
            />
            <MetricCard
              label="Total OT Hours"
              icon={<Clock className="w-4 h-4" />}
              value={metrics.totalOtHours.toLocaleString()}
              unit="hrs"
            />
            <MetricCard
              label="Total OT Amount"
              icon={<Wallet className="w-4 h-4" />}
              value={metrics.totalOtAmount}
            />
            <MetricCard
              label="Pending"
              icon={<Hourglass className="w-4 h-4" />}
              value={metrics.pendingHours}
              unit="hrs"
              tone="amber"
            />
            <MetricCard
              label="Approved"
              icon={<CheckCircle2 className="w-4 h-4" />}
              value={metrics.approvedHours.toLocaleString()}
              unit="hrs"
              tone="emerald"
            />
            <MetricCard
              label="Rejected"
              icon={<XCircle className="w-4 h-4" />}
              value={metrics.rejectedHours}
              unit="hrs"
              tone="red"
            />
          </div>

          {/* Employee Overtime Trends */}
          <ChartCard
            title="Employee Overtime Trends"
            filters={[
              { label: "Year", options: ["2026", "2025"] },
              { label: "Month", options: ["All Months", "August", "July"] },
            ]}
          >
            {trendsChart ?? (
              <div className="w-full h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mockTrendsData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <Tooltip 
                      cursor={{ fill: '#f3f4f6' }}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Bar dataKey="hours" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>

          {/* Departmental OT Breakdown */}
          <ChartCard
            title="Departmental OT Breakdown"
            filters={[
              { label: "Department", options: ["All Departments", "Production", "QA"] },
              { label: "Period", options: ["This Month", "Last Month"] },
            ]}
          >
            {departmentChart ?? (
              <div className="w-full h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mockDepartmentData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} width={80} />
                    <Tooltip
                      cursor={{ fill: '#f3f4f6' }}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Bar dataKey="hours" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>

          {/* Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <EmployeeTable
              title="Top OT Employees"
              employees={topEmployees}
              showViewAll
              onViewAll={onViewAllTopEmployees}
            />
            <EmployeeTable title="Lowest OT Employees" employees={lowestEmployees} />
          </div>
      </div>
    </div>
  );
}